// src/features/services/media-v2/hooks/useMediaV2.ts
// ─────────────────────────────────────────────────────────────────────────────
// Media V2 — hook-owns-all-state.
// Owns: fetch, mutations, cache invalidation, SSE streaming for
// YouTube ingest, audio library, and music production workflows.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAudioLibraryApi,
  fetchAudioEntryApi,
  fetchMusicProjectsApi,
  fetchMusicProjectApi,
  ingestYoutubeApi,
  createMusicProjectApi,
  releaseMusicProjectApi,
  importMusicProjectApi,
  subscribeMediaStream,
  QK_AUDIO_LIBRARY,
  QK_MUSIC_PROJECTS,
  type AudioLibraryResponse,
  type AudioEntry,
  type MusicProjectListResponse,
  type MusicProject,
  type IngestResult,
  type ReleaseResult,
  type YouTubeIngestPayload,
  type CreateProjectPayload,
  type ReleaseProjectPayload,
  type ImportProjectPayload,
  type MediaStreamEvent,
} from '../api/mediaV2Api';

// ── Pipeline streaming state ─────────────────────────────────────────────

export interface MediaStreamingState {
  scopeKey: string | null;
  stage: string | null;
  events: MediaStreamEvent[];
  active: boolean;
  error: string | null;
  runId: string | null;
}

const IDLE_STREAM: MediaStreamingState = {
  scopeKey: null,
  stage: null,
  events: [],
  active: false,
  error: null,
  runId: null,
};

// ── Audio Library hook ───────────────────────────────────────────────────

export function useAudioLibrary(params?: { prefix?: string; category?: string; limit?: number }) {
  return useQuery<AudioLibraryResponse, Error>({
    queryKey: [...QK_AUDIO_LIBRARY, params],
    queryFn: () => fetchAudioLibraryApi(params),
    staleTime: 30_000,
  });
}

export function useAudioEntry(key: string | null) {
  return useQuery<AudioEntry, Error>({
    queryKey: [...QK_AUDIO_LIBRARY, key],
    queryFn: () => fetchAudioEntryApi(key!),
    enabled: !!key,
    staleTime: 60_000,
  });
}

// ── Music Projects hook ──────────────────────────────────────────────────

export function useMusicProjects(params?: { limit?: number }) {
  return useQuery<MusicProjectListResponse, Error>({
    queryKey: [...QK_MUSIC_PROJECTS, params],
    queryFn: () => fetchMusicProjectsApi(params),
    staleTime: 30_000,
  });
}

export function useMusicProject(key: string | null) {
  return useQuery<MusicProject, Error>({
    queryKey: [...QK_MUSIC_PROJECTS, key],
    queryFn: () => fetchMusicProjectApi(key!),
    enabled: !!key,
    staleTime: 60_000,
  });
}

// ── YouTube Ingest hook (with SSE streaming) ─────────────────────────────

export function useYoutubeIngest() {
  const qc = useQueryClient();
  const [streaming, setStreaming] = useState<MediaStreamingState>(IDLE_STREAM);
  const streamRef = useRef<{ close: () => void } | null>(null);

  useEffect(() => () => { streamRef.current?.close(); }, []);

  const startStream = useCallback((scopeKey: string) => {
    streamRef.current?.close();

    setStreaming({
      scopeKey,
      stage: 'triggering',
      events: [],
      active: true,
      error: null,
      runId: null,
    });

    const handle = subscribeMediaStream(scopeKey, {
      onStage: (event) => {
        setStreaming(prev => ({
          ...prev,
          stage: event.stage,
          events: [...prev.events, event],
          error: event.stage === 'failed' ? (event.error ?? 'Pipeline failed') : prev.error,
          runId: event.run_id ?? prev.runId,
        }));
      },
      onDone: () => {
        setStreaming(prev => ({ ...prev, active: false }));
        qc.invalidateQueries({ queryKey: [...QK_AUDIO_LIBRARY] });
        streamRef.current = null;
      },
    });

    streamRef.current = handle;
  }, [qc]);

  const clearStream = useCallback(() => {
    streamRef.current?.close();
    streamRef.current = null;
    setStreaming(IDLE_STREAM);
  }, []);

  const ingest = useMutation<IngestResult, Error, YouTubeIngestPayload>({
    mutationFn: (p) => ingestYoutubeApi(p),
    onSuccess: (result) => {
      startStream(result.scope_key);
    },
  });

  return { ingest, streaming, clearStream };
}

// ── Music Project Lifecycle hooks ────────────────────────────────────────

export function useMusicProjectCreate() {
  const qc = useQueryClient();
  const [streaming, setStreaming] = useState<MediaStreamingState>(IDLE_STREAM);
  const streamRef = useRef<{ close: () => void } | null>(null);

  useEffect(() => () => { streamRef.current?.close(); }, []);

  const startStream = useCallback((scopeKey: string) => {
    streamRef.current?.close();

    setStreaming({
      scopeKey,
      stage: 'triggering',
      events: [],
      active: true,
      error: null,
      runId: null,
    });

    const handle = subscribeMediaStream(scopeKey, {
      onStage: (event) => {
        setStreaming(prev => ({
          ...prev,
          stage: event.stage,
          events: [...prev.events, event],
          error: event.stage === 'failed' ? (event.error ?? 'Pipeline failed') : prev.error,
          runId: event.run_id ?? prev.runId,
        }));
      },
      onDone: () => {
        setStreaming(prev => ({ ...prev, active: false }));
        qc.invalidateQueries({ queryKey: [...QK_MUSIC_PROJECTS] });
        streamRef.current = null;
      },
    });

    streamRef.current = handle;
  }, [qc]);

  const clearStream = useCallback(() => {
    streamRef.current?.close();
    streamRef.current = null;
    setStreaming(IDLE_STREAM);
  }, []);

  const create = useMutation<IngestResult, Error, CreateProjectPayload>({
    mutationFn: (p) => createMusicProjectApi(p),
    onSuccess: (result) => {
      startStream(result.scope_key);
    },
  });

  return { create, streaming, clearStream };
}

export function useMusicProjectRelease() {
  const qc = useQueryClient();

  return useMutation<ReleaseResult, Error, ReleaseProjectPayload>({
    mutationFn: (p) => releaseMusicProjectApi(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...QK_MUSIC_PROJECTS] });
    },
  });
}

/**
 * Save / import a project directly from FE-provided data (no pipeline).
 * Used by the Music subtab "Import" + "Save" controls. Invalidates the
 * project library so the new/updated entry appears immediately.
 */
export function useMusicProjectImport() {
  const qc = useQueryClient();

  return useMutation<MusicProject, Error, ImportProjectPayload>({
    mutationFn: (p) => importMusicProjectApi(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...QK_MUSIC_PROJECTS] });
    },
  });
}
